/**
 * Playbook Detail Page (Spec 060)
 *
 * Shows a single playbook with actions grouped by severity, inline status updates.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router';
import { apiGet, apiFetch } from '../lib/api.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';

// Phase 9 Session D (gap 087-5). Matches the canonical UUID regex used
// elsewhere in the app — loose enough for RFC 4122 v4 variants.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface PlaybookAction {
  id: string;
  order: number;
  action_type: string;
  title: string;
  description: string | null;
  rationale: string | null;
  target_urls: string[] | null;
  target_engines: string[] | null;
  effort_estimate: string | null;
  status: string;
  completed_at: string | null;
  notes: string | null;
}

interface PlaybookDetail {
  id: string;
  brand_id: string;
  title: string;
  status: string;
  priority: string | null;
  summary: string | null;
  baseline_snapshot: Record<string, unknown> | null;
  error_message: string | null;
  generated_at: string | null;
  created_at: string;
  updated_at: string;
  actions: PlaybookAction[];
}

/** Boxicon names, not components. Spec 230. */
const STATUS_ICONS: Record<string, string> = {
  todo: 'bx-circle',
  in_progress: 'bx-time-five',
  done: 'bx-check-circle',
  skipped: 'bx-x-circle',
};

const STATUS_COLORS: Record<string, string> = {
  todo: 'text-gray-400',
  in_progress: 'text-blue-500',
  done: 'text-green-500',
  skipped: 'text-yellow-500',
};

const ACTION_STATUSES = ['todo', 'in_progress', 'done', 'skipped'] as const;

const SEVERITY_ORDER = ['critical', 'high', 'maintenance'] as const;
const SEVERITY_COLORS: Record<string, string> = {
  critical: 'border-red-200 bg-red-50',
  high: 'border-yellow-200 bg-yellow-50',
  maintenance: 'border-green-200 bg-green-50',
};

export function PlaybookDetailPage() {
  const { playbookId } = useParams<{ playbookId: string }>();
  const [playbook, setPlaybook] = useState<PlaybookDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Phase 9 Session D (gap 087-5). The chip is purely informational —
  // we derive it from URL params so a user can deep-link from the
  // Coverage Analysis page and dismiss without touching router state
  // outside this page. Dismissing clears the params via
  // `setSearchParams({})` so the chip won't re-appear until the user
  // navigates back in with them.
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceType = searchParams.get('source_type');
  const analysisId = searchParams.get('analysis_id');
  const coverageContext = useMemo(() => {
    if (sourceType !== 'coverage_analysis') return null;
    if (!analysisId || !UUID_RE.test(analysisId)) return null;
    return { analysisId };
  }, [sourceType, analysisId]);

  const dismissCoverageChip = () => {
    // Replace — not push — so the back button still points to the
    // Coverage Analysis detail page the user came from.
    setSearchParams({}, { replace: true });
  };

  const fetchPlaybook = useCallback(async () => {
    if (!playbookId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet<{ data: PlaybookDetail }>(`/api/v1/playbooks/${playbookId}`);
      setPlaybook(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load playbook');
    } finally {
      setLoading(false);
    }
  }, [playbookId]);

  useEffect(() => { fetchPlaybook(); }, [fetchPlaybook]);

  const updateActionStatus = async (actionId: string, status: string) => {
    if (!playbookId) return;
    try {
      await apiFetch(`/api/v1/playbooks/${playbookId}/actions/${actionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      fetchPlaybook();
    } catch { /* ignore */ }
  };

  const updatePlaybookStatus = async (status: string) => {
    if (!playbookId) return;
    try {
      await apiFetch(`/api/v1/playbooks/${playbookId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      fetchPlaybook();
    } catch { /* ignore */ }
  };

  // Render the coverage-analysis chip regardless of load/error state so
  // users who arrive via the Coverage deep-link always see the context
  // even if the playbook isn't yet available (or has been deleted).
  const coverageChip = coverageContext ? (
    <div
      data-testid="playbook-coverage-chip"
      className="mt-4 flex items-start gap-3 rounded-md border border-violet-300 bg-violet-50 px-4 py-3 text-sm text-violet-900"
    >
      <Icon name="bx-layer" className="mt-0.5 shrink-0 text-violet-600 text-lg" />
      <div className="flex-1 space-y-1">
        <p>
          Viewing playbook items from coverage analysis{' '}
          <code className="rounded-md bg-violet-100 px-1 font-mono text-xs text-violet-800">
            {coverageContext.analysisId.slice(0, 8)}
          </code>
        </p>
        <Link
          to={`/coverage/${coverageContext.analysisId}`}
          data-testid="playbook-coverage-back"
          className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:underline"
        >
          <Icon name="bx-left-arrow-alt" className="text-xs" />
          Back to coverage analysis
        </Link>
      </div>
      <button
        type="button"
        data-testid="playbook-coverage-dismiss"
        onClick={dismissCoverageChip}
        aria-label="Dismiss coverage analysis context"
        className="shrink-0 rounded-md p-1 text-violet-700 transition-colors hover:bg-violet-100 hover:text-violet-900"
      >
        <Icon name="bx-x" className="text-base" />
      </button>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="hub-page--reading" data-testid="playbook-detail-page">
        <Link to="/playbooks" className="flex items-center gap-1 text-sm text-violet-600 hover:underline"><Icon name="bx-left-arrow-alt" />Back to playbooks</Link>
        {coverageChip}
        <div className="flex justify-center pt-16">
          <Spinner className="text-3xl text-violet-600" />
        </div>
      </div>
    );
  }

  if (error || !playbook) {
    return (
      <div className="hub-page--reading" data-testid="playbook-detail-page">
        <Link to="/playbooks" className="flex items-center gap-1 text-sm text-violet-600 hover:underline"><Icon name="bx-left-arrow-alt" />Back to playbooks</Link>
        {coverageChip}
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error ?? 'Playbook not found'}</div>
      </div>
    );
  }

  const doneCount = playbook.actions.filter((a) => a.status === 'done').length;
  const totalCount = playbook.actions.length;
  const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  // Group actions by severity (inferred from action_type or order)
  const grouped: Record<string, PlaybookAction[]> = { critical: [], high: [], maintenance: [] };
  for (const action of playbook.actions) {
    // Use order to infer severity: first third = critical, second = high, rest = maintenance
    const idx = playbook.actions.indexOf(action);
    const third = Math.ceil(totalCount / 3);
    if (idx < third) grouped['critical']!.push(action);
    else if (idx < third * 2) grouped['high']!.push(action);
    else grouped['maintenance']!.push(action);
  }

  return (
    <div className="hub-page--reading" data-testid="playbook-detail-page">
      <Breadcrumbs
        className="mb-3"
        items={[{ label: 'Playbooks', to: '/playbooks' }, { label: playbook.title }]}
      />
      <Link to="/playbooks" className="flex items-center gap-1 text-sm text-violet-600 hover:underline"><Icon name="bx-left-arrow-alt" />Back to playbooks</Link>

      {coverageChip}

      <div className="mt-4 flex items-start justify-between">
        <div>
          <h1 className="hub-page__title">{playbook.title}</h1>
          <p className="mt-1 text-sm text-slate-500">
            Generated {playbook.generated_at ? new Date(playbook.generated_at).toLocaleDateString() : 'N/A'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
            playbook.status === 'active' ? 'bg-green-100 text-green-700' :
            playbook.status === 'completed' ? 'bg-blue-100 text-blue-700' :
            playbook.status === 'generating' ? 'bg-yellow-100 text-yellow-700' :
            'bg-gray-100 text-gray-700'
          }`}>{playbook.status}</span>
          {playbook.status === 'draft' && (
            <Button variant="primary" onClick={() => updatePlaybookStatus('active')}>
              Activate
            </Button>
          )}
          {playbook.status === 'active' && doneCount === totalCount && (
            <Button variant="primary" onClick={() => updatePlaybookStatus('completed')}>
              Mark complete
            </Button>
          )}
        </div>
      </div>

      {playbook.summary && (
        <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{playbook.summary}</p>
      )}

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{doneCount} of {totalCount} actions complete</span>
          <span>{progressPct}%</span>
        </div>
        <div className="mt-1 h-2 rounded-full bg-gray-200">
          <div className="h-2 rounded-full bg-violet-600 transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Actions grouped by severity */}
      {SEVERITY_ORDER.map((severity) => {
        const actions = grouped[severity] ?? [];
        if (actions.length === 0) return null;
        return (
          <section key={severity} className="hub-section">
            <div className="hub-section__head">
              <h2 className="text-base font-semibold text-slate-900">
                {severity.charAt(0).toUpperCase() + severity.slice(1)} actions
              </h2>
              <span className="text-sm text-[var(--hub-color-ink-subtle)]">
                {actions.length} action{actions.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className={`space-y-2 rounded-lg border p-3 ${SEVERITY_COLORS[severity] ?? ''}`}>
              {actions.map((action) => {
                const statusIcon = STATUS_ICONS[action.status] ?? 'bx-circle';
                return (
                  <div key={action.id} className="rounded-md bg-white p-3" data-testid={`action-${action.id}`}>
                    <div className="flex items-start gap-3">
                      <Icon name={statusIcon} className={`mt-0.5 text-lg ${STATUS_COLORS[action.status] ?? 'text-gray-400'}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-900">{action.title}</h3>
                          <select
                            value={action.status}
                            onChange={(e) => updateActionStatus(action.id, e.target.value)}
                            className="rounded-md border border-gray-200 px-1.5 py-0.5 text-xs text-gray-600"
                          >
                            {ACTION_STATUSES.map((s) => (
                              <option key={s} value={s}>{s.replace('_', ' ')}</option>
                            ))}
                          </select>
                        </div>
                        {action.description && <p className="mt-1 text-xs text-gray-600">{action.description}</p>}
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                          <span className="rounded-md bg-gray-100 px-1.5 py-0.5">{action.action_type}</span>
                          {action.effort_estimate && <span>Effort: {action.effort_estimate}</span>}
                          {action.target_urls && action.target_urls.length > 0 && (
                            <span>{action.target_urls.length} target URL{action.target_urls.length > 1 ? 's' : ''}</span>
                          )}
                        </div>
                        {action.rationale && <p className="mt-1 text-xs text-gray-400">{action.rationale}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
